import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { animated, useTransition } from '@react-spring/web';
import { useState } from 'react';
import styled from 'styled-components';

import { AveragePlayerScore } from '@sorare/core/src/__generated__/globalTypes';
import { CardFooter } from '@sorare/core/src/components/card/CardFooter';
import { DropRank } from '@sorare/core/src/components/commonGame/DropRank';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';

import { AnyPlayerStatsIcon } from '#src/components/player/AnyPlayerStatsIcon/index.tsx';
import { PlayerDrawer } from '#src/components/player/PlayerDrawer/index.tsx';

import type { CardInfos_anyCardInterface } from './__generated__/index.graphql.ts';

const StyledAnimatedDiv = styled(animated.div)`
  position: absolute;
  bottom: calc(-1 * var(--margin-bottom-center-area));
`;

type Props = {
  card: CardInfos_anyCardInterface;
  showPlayerDetails?: boolean;
};
export const CardInfos = ({ card, showPlayerDetails }: Props) => {
  const [playerDetailsOpen, setPlayerDetailsOpen] = useState(false);
  const transition = useTransition(card, {
    from: { opacity: 0, transform: 'translate3d(0, -32px ,0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0 ,0)', delay: 300 },
    leave: {
      opacity: 0,
      transform: 'translate3d(0, 0 ,0)',
      config: { duration: 100 },
    },
    exitBeforeEnter: true,
  });

  return (
    <>
      {transition((styles, currentCard) => {
        const dropRank =
          typeof currentCard.dropRank === 'number' ? currentCard.dropRank : 5;
        return (
          <StyledAnimatedDiv style={styles}>
            <CardFooter
              left={
                <AnyPlayerStatsIcon
                  backgroundColor="transparent"
                  player={currentCard.anyPlayer}
                />
              }
              right={<DropRank dropRank={dropRank} />}
              topGap={0.5}
              onClick={
                showPlayerDetails
                  ? () => {
                      setPlayerDetailsOpen(true);
                    }
                  : undefined
              }
            >
              <PlayerScore
                player={currentCard.anyPlayer}
                mode={AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE}
                animated={false}
                hideTooltip
              />
            </CardFooter>
          </StyledAnimatedDiv>
        );
      })}
      {!!showPlayerDetails && (
        <PlayerDrawer
          cardOpened={
            playerDetailsOpen
              ? {
                  playerSlug: card.anyPlayer.slug,
                  cardSlug: card.slug,
                }
              : null
          }
          onClose={() => {
            setPlayerDetailsOpen(false);
          }}
        />
      )}
    </>
  );
};

CardInfos.fragments = {
  anyCardInterface: gql`
    fragment CardInfos_anyCardInterface on AnyCardInterface {
      slug
      dropRank
      anyPlayer {
        slug
        ...AnyPlayerStatsIcon_anyPlayer
        ...PlayerScore_anyPlayer
      }
    }
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${PlayerScore.fragments.anyPlayer}
  ` as TypedDocumentNode<CardInfos_anyCardInterface>,
};
